import type { RootState } from "@/app/store";
import { db } from "@/lib/db";
import type { Notification } from "@/types";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

/** Syncs the notification table into IndexedDB; the UI reads notifications from Dexie. */
export const fetchNotifications = createAsyncThunk<void, FeatureLayer>(
    'notifications/fetchNotifications',
    async (notificationTable) => {
        const lastNotification = await db.notifications.orderBy('createdAt').last();
        const results = await notificationTable.queryFeatures({
            where: `1=1`,
            outFields: ['*']
        });
        const newNotifications = results.features
            .filter((feature) => !lastNotification || feature.attributes.CreationDate > lastNotification.createdAt)
            .map((feature) => ({
                objectId: feature.attributes.OBJECTID,
                title: feature.attributes.Name,
                message: feature.attributes.Description,
                linkUrl: feature.attributes.LinkUrl,
                createdAt: feature.attributes.CreationDate,
                viewed: false
            }));
        await db.notifications.bulkPut(newNotifications as Notification[]);

        // Keep one row per notification, and drop rows no longer published.
        const allNotifications = await db.notifications.toArray();
        const latestIds = new Map(allNotifications.map((n) => [n.objectId, n.id]));
        const publishedIds = new Set(results.features.map((feature) => feature.attributes.OBJECTID));
        const stale = allNotifications.filter((n) => latestIds.get(n.objectId) !== n.id || !publishedIds.has(n.objectId));
        await db.notifications.bulkDelete(stale.map((n) => n.id));
    }
)

export interface NotificationsState {
    loading: boolean
}

export const initialState: NotificationsState = {
    loading: false
}

export const notificationsSlice = createSlice({
    name: 'notifications',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchNotifications.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(fetchNotifications.fulfilled, (state) => {
            state.loading = false;
        });
        builder.addCase(fetchNotifications.rejected, (state) => {
            state.loading = false;
        });
    }
})

export const notificationsLoading = (state: RootState) => state.notifications.loading;

export default notificationsSlice.reducer;
